import type React from "react";
import { useState } from "react";

// Yes/No confirm buttons for the confirm-gate turn of a portal workflow (e.g.
// "Want me to place this order?"). Tapping a button sends its message down the
// SAME path a typed "go ahead" takes (onSendFollowup) — the model's confirm
// gate is satisfied without the clinician having to type. After a choice the
// pair stays in the thread, disabled, with the chosen side highlighted: it
// can't be double-submitted and the thread still shows what was answered.
export interface WorkflowConfirmData {
  confirm_label?: string;
  decline_label?: string;
  confirm_message?: string;
  decline_message?: string;
  // Restored from history: which side was taken on an earlier turn.
  chosen_label?: string;
}

interface WorkflowConfirmProps {
  data: WorkflowConfirmData;
  onSendFollowup: (message: string) => void;
  disabled?: boolean;
  // This confirm belongs to an earlier turn — render it read-only.
  frozen?: boolean;
}

const WorkflowConfirm: React.FC<WorkflowConfirmProps> = ({ data, onSendFollowup, disabled, frozen }) => {
  const [choseLabel, setChoseLabel] = useState<string | null>(null);

  const confirmLabel = data?.confirm_label || "Yes, go ahead";
  const declineLabel = data?.decline_label || "No, not now";
  const confirmMessage = data?.confirm_message || confirmLabel;
  const declineMessage = data?.decline_message || declineLabel;

  const chosen = choseLabel || data?.chosen_label || null;
  // A frozen confirm with no recorded pick is still inert — the turn is over.
  const readOnly = !!frozen || !!chosen;

  const choose = (label: string, message: string) => {
    if (disabled || readOnly) return;
    setChoseLabel(label);
    onSendFollowup(message);
  };

  const buttonStyle = (label: string, base: React.CSSProperties): React.CSSProperties => ({
    ...styles.button,
    ...base,
    ...(disabled || readOnly ? styles.disabled : {}),
    ...(readOnly && chosen === label ? styles.chosen : {}),
    ...(readOnly && chosen !== label ? styles.notChosen : {}),
  });

  return (
    <div style={styles.container}>
      <button
        type="button"
        disabled={disabled || readOnly}
        onClick={() => choose(confirmLabel, confirmMessage)}
        style={buttonStyle(confirmLabel, styles.confirm)}
      >
        {confirmLabel}
      </button>
      <button
        type="button"
        disabled={disabled || readOnly}
        onClick={() => choose(declineLabel, declineMessage)}
        style={buttonStyle(declineLabel, styles.decline)}
      >
        {declineLabel}
      </button>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  // textAlign defends against ancestors that center-align text.
  container: {
    display: "flex",
    gap: "8px",
    marginTop: "8px",
    flexWrap: "wrap",
    alignItems: "center",
    textAlign: "left",
  },
  button: {
    padding: "8px 16px",
    fontSize: "13px",
    fontWeight: 500,
    borderRadius: "8px",
    border: "1px solid transparent",
    cursor: "pointer",
    fontFamily: "inherit",
  },
  confirm: { backgroundColor: "#4338CA", color: "#FFFFFF", borderColor: "#4338CA" },
  decline: { backgroundColor: "#FFFFFF", color: "#374151", borderColor: "#D1D5DB" },
  disabled: { opacity: 0.6, cursor: "not-allowed" },
  // The option that wasn't taken recedes; the chosen one keeps its colour so
  // the answer is readable at a glance when scrolling back.
  chosen: { opacity: 1 },
  notChosen: { opacity: 0.35 },
};

export default WorkflowConfirm;
